import { useEffect, useRef, useState, useMemo, type ReactNode } from "react";
import { Info } from "lucide-react";
import { timezoneToCountryCode, countryNameToISO2, countryCodeToName } from "@/utils/timezoneToCountry";
import CountryFlag from "@/components/common/CountryFlag";
import { isImpersonated } from "@/hooks/api/auth/tokenManager";
import { EMERGENCY_CONTACT_WEBHOOK } from "@/hooks/api/config";
import { exportClinicalHistoryPdf } from "@/utils/exportClinicalHistoryPdf";
import { generateConstanciaPdf } from "@/utils/generateConstanciaPdf";
import { buildConstanciaSegments, type ConstanciaSourceSession } from "@/utils/constanciaSessions";
import InfoTooltip from "@/components/common/InfoTooltip";
import { usePsychologistContext } from "@/contexts/PsychologistContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePatientDetail } from "@/hooks/patients/usePatientDetail";
import type { PatientDetailData } from "@/hooks/patients/usePatientDetail";
import apiCoreAdapter, { type ProcessDerivation, type PatientPreviousProcess } from "@/hooks/apiCoreAdapter";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import type { Patient } from "./PatientTable";
import {
  formatBirthDateDdMmYyyy,
  getTimeZoneOffsetMinutes,
  formatDiffLabel,
  formatDateInTimezone,
} from "../utils/patientFormatters";
import { getPaymentCountdown, type PaymentCountdownInfo } from "@/utils/paymentCountdown";
import { BA_TIMEZONE, parseBackendSessionDate } from "@/utils/timezone";
import { isEffectiveSessionStatus } from "@/utils/sessions";

// El endpoint real de backend para el historial completo multi-profesional todavía no está
// listo — el botón queda oculto hasta entonces. Toda la lógica (fetch, armado de segmentos,
// generación del PDF) sigue intacta; para reactivarlo alcanza con volver esto a `true`.
const CONSTANCIA_FEATURE_ENABLED = false;

interface PatientDetailModalProps {
  patient: Patient;
  onClose: () => void;
  initialTab?: "info" | "sessions" | "derivation" | "clinical_history";
  autoOpenNote?: boolean;
  /** Notifica el cambio de pestaña interna para reflejarlo en la URL (deep-linking) */
  onTabChange?: (tab: "info" | "sessions" | "derivation" | "clinical_history") => void;
}

type Session = { id: string; date: string; hasNote?: boolean; note?: string; status?: string };

/**
 * Contexto que determina si/cómo se muestra la pestaña "Derivación":
 * - `formal`: existe una derivación registrada con `to` = proceso actual.
 * - `informal`: sin derivación formal, pero el paciente tiene un proceso anterior con otro
 *   psicólogo que tuvo al menos una sesión efectiva ("baja y retorno").
 * `previousProcess` es el match (si existe) en `previousProcesses` para enriquecer con
 * fechas/sesiones efectivas — puede faltar si el endpoint de procesos anteriores todavía
 * no está disponible en el backend.
 */
type DerivationContext =
  | { kind: "formal"; derivation: ProcessDerivation; previousProcess: PatientPreviousProcess | null }
  | { kind: "informal"; previousProcess: PatientPreviousProcess };

/**
 * `GET /v1/psi-platform/psychologist/patients/{processId}/previous-processes` todavía no
 * existe en api-core (ver `PatientPreviousProcess` en apiCoreAdapter, "ENDPOINT A CONSTRUIR").
 * Mientras no exista, el llamado falla siempre y no aporta nada, así que queda apagado.
 *
 * Qué se pierde mientras esté en `false`:
 *  - la detección de "baja y retorno" informal (pestaña "Derivación" de tipo `informal`),
 *    que hoy tampoco funcionaba porque depende de este mismo endpoint;
 *  - en las derivaciones formales, la línea de fechas del proceso anterior
 *    (`activation_date – last_session_date`). El resto del tab se arma con la derivación.
 *
 * PARA REACTIVAR cuando el endpoint esté construido: poner en `true`. No hace falta tocar
 * nada más — el efecto y el parseo de la respuesta quedaron intactos.
 */
const PREVIOUS_PROCESSES_ENABLED = false;

/**
 * `GET /v1/psi-platform/psychologist/patients/{processId}/full-session-history` tampoco existe
 * todavía en api-core (ver `getPatientFullSessionHistory` en apiCoreAdapter). Alimenta el
 * historial completo que usa la Constancia; mientras no exista, la Constancia se arma solo con
 * las sesiones del proceso actual.
 *
 * PARA REACTIVAR cuando el endpoint esté construido: poner en `true`.
 */
const FULL_SESSION_HISTORY_ENABLED = false;

/**
 * Notificación a Make del contacto de emergencia. Desactivada: el guardado real ya ocurre vía
 * `apiCoreAdapter.saveEmergencyContact` y este webhook es solo un aviso al backoffice.
 *
 * PARA REACTIVAR: poner en `true` y definir `REACT_APP_EMERGENCY_CONTACT_WEBHOOK` en el `.env`
 * (sin esa var la constante queda en '' y `fetch('')` pega contra la propia página).
 */
const EMERGENCY_CONTACT_WEBHOOK_ENABLED = false;

/**
 * CRUD del contacto de emergencia está servido por psiplatform-be
 * (`/api/psychologist/patients/{processId}/emergency-contact[/:id]`, Supabase),
 * no por api-core. Un proceso puede tener N contactos (ej. madre y padre) —
 * cada uno se identifica por `id`, ya no por `process_id` (dejó de ser único).
 * Precarga habilitada.
 */
const EMERGENCY_CONTACT_ENABLED = true;

// --- Component ---

// ─── Emergency contact ─────────────────────────────────────────────────────

interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

type EcEntry = {
  /** `null` = tarjeta nueva, todavía no persistida en el backend. */
  id: string | null;
  data: EmergencyContact;
  editMode: boolean;
  form: EmergencyContact;
  saving: boolean;
  deleting: boolean;
  deleteConfirming: boolean;
};

const EC_EMPTY: EmergencyContact = { name: "", phone: "", relationship: "" };

// ──────────────────────────────────────────────────────────────────────────────

export function PatientDetailModal({ patient, onClose, initialTab, autoOpenNote, onTabChange }: PatientDetailModalProps) {
  const processId = patient.processId || patient.id;
  const patientId = patient.id !== patient.processId ? patient.id : undefined;
  const { data, isLoading, isLoadingSessions } = usePatientDetail(processId, patientId);
  const detail = data as PatientDetailData | null;
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const therapistCountry = countryCodeToName(timezoneToCountryCode(therapistTz)) || 'Argentina';
  const { psychologistId: currentPsychologistId } = useAuth();

  const [activeTab, setActiveTabState] = useState<"info" | "sessions" | "derivation" | "clinical_history">(initialTab ?? "info");
  // Wrapper: refleja en la URL cada cambio de pestaña (si el consumidor lo soporta)
  const setActiveTab = (tab: "info" | "sessions" | "derivation" | "clinical_history") => {
    setActiveTabState(tab);
    onTabChange?.(tab);
  };
  const [sessionsState, setSessionsState] = useState<Session[]>([]);
  const [derivations, setDerivations] = useState<ProcessDerivation[]>([]);
  const [derivationsLoading, setDerivationsLoading] = useState(false);
  const [previousProcesses, setPreviousProcesses] = useState<PatientPreviousProcess[]>([]);
  const [previousProcessesLoading, setPreviousProcessesLoading] = useState(false);
  const [sessionNotes, setSessionNotes] = useState<SessionNote[]>([]);
  const [sessionNotesLoading, setSessionNotesLoading] = useState(false);
  const [fullSessionHistory, setFullSessionHistory] = useState<ConstanciaSourceSession[]>([]);
  const [fullHistoryLoading, setFullHistoryLoading] = useState(true);
  const [generatingConstancia, setGeneratingConstancia] = useState(false);
  const [expandedSession, setExpandedSession] = useState<{ sessionId: string; mode: "add" | "view" } | null>(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [reasonExpanded, setReasonExpanded] = useState(false);
  const [reasonCanToggle, setReasonCanToggle] = useState(false);

  // Emergency contact state
  const [ecContacts, setEcContacts] = useState<EcEntry[]>([]);
  const [ecReady, setEcReady] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const reasonRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  // Sync sessions from data
  useEffect(() => {
    const sessions: Session[] = (detail?.sessions as Session[]) || [];
    setSessionsState(sessions);
  }, [detail?.sessions]);

  // Cargar notas de sesión al abrir el modal
  useEffect(() => {
    let cancelled = false;
    setSessionNotesLoading(true);
    apiCoreAdapter.getPatientSessionNotes(processId)
      .then((data) => { if (!cancelled) setSessionNotes(data); })
      .catch(() => { if (!cancelled) setSessionNotes([]); })
      .finally(() => { if (!cancelled) setSessionNotesLoading(false); });
    return () => { cancelled = true; };
  }, [processId]);

  // Cargar derivaciones del proceso siempre que se abra el modal.
  // (No se condiciona a patient.isReferred: en deep-links por URL no conocemos ese flag,
  //  y la pestaña "Derivación" se muestra según `derivationContext`, ver más abajo.)
  useEffect(() => {
    let cancelled = false;
    setDerivationsLoading(true);
    apiCoreAdapter.getProcessDerivations(processId)
      .then((data) => { if (!cancelled) setDerivations(data); })
      .catch(() => { if (!cancelled) setDerivations([]); })
      .finally(() => { if (!cancelled) setDerivationsLoading(false); });
    return () => { cancelled = true; };
  }, [processId]);

  // Cargar otros procesos del paciente (para detectar "baja y retorno" informal y para
  // enriquecer "Proceso anterior" con fechas/sesiones efectivas). Endpoint aún no existe en
  // api-core (ver PatientPreviousProcess) — si falla, se trata como "sin procesos anteriores".
  //
  // DESACTIVADO (ver PREVIOUS_PROCESSES_ENABLED): el llamado fallaba siempre y, cuando el
  // fallo era de red, los reintentos de secureHttp (30s + 60s) dejaban la pestaña
  // "Derivación" hasta 90s en "Cargando derivación..." para no mostrar nada distinto.
  useEffect(() => {
    if (!PREVIOUS_PROCESSES_ENABLED) {
      setPreviousProcesses([]);
      setPreviousProcessesLoading(false);
      return;
    }
    let cancelled = false;
    setPreviousProcessesLoading(true);
    apiCoreAdapter.getPatientPreviousProcesses(processId)
      .then((data) => { if (!cancelled) setPreviousProcesses(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPreviousProcesses([]); })
      .finally(() => { if (!cancelled) setPreviousProcessesLoading(false); });
    return () => { cancelled = true; };
  }, [processId]);

  // Cargar historial completo de sesiones (todos los procesos/profesionales) para la Constancia.
  // DESACTIVADO (ver FULL_SESSION_HISTORY_ENABLED): mismo caso que previous-processes, la ruta
  // no existe en api-core. El default de fullHistoryLoading es true, así que hay que bajarlo
  // a mano para no dejar la Constancia con el spinner colgado.
  useEffect(() => {
    if (!FULL_SESSION_HISTORY_ENABLED) {
      setFullSessionHistory([]);
      setFullHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setFullHistoryLoading(true);
    apiCoreAdapter.getPatientFullSessionHistory(processId)
      .then((data) => { if (!cancelled) setFullSessionHistory(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setFullSessionHistory([]); })
      .finally(() => { if (!cancelled) setFullHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [processId]);

  const constanciaSegments = useMemo(() => buildConstanciaSegments(fullSessionHistory), [fullSessionHistory]);

  const handleGenerateConstancia = async () => {
    if (constanciaSegments.length === 0) return;
    setGeneratingConstancia(true);
    try {
      await generateConstanciaPdf({
        patientFullName: patient.name || detail?.fullname || "",
        segments: constanciaSegments,
      });
      toast.success("Constancia generada");
    } catch {
      toast.error("No se pudo generar la constancia");
    } finally {
      setGeneratingConstancia(false);
    }
  };

  // Detect reason overflow for expand/collapse
  useEffect(() => {
    const el = reasonRef.current;
    if (!el) { setReasonCanToggle(false); return; }
    if (!reasonExpanded) {
      requestAnimationFrame(() => {
        try {
          const can = el.scrollHeight > el.clientHeight + 1;
          setReasonCanToggle(can);
        } catch {
          setReasonCanToggle(false);
        }
      });
    } else {
      setReasonCanToggle(true);
    }
  }, [reasonExpanded, detail?.reason]);

  // Auto-open note form for most recent past session when autoOpenNote=true
  const autoOpenDoneRef = useRef(false);
  useEffect(() => {
    if (!autoOpenNote || autoOpenDoneRef.current || sessionsState.length === 0) return;
    const sorted = sessionsState.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const now = new Date();
    const past = sorted.filter(s => new Date(s.date) < now);
    const noteLimit = derivations.length > 0 ? past.length : 6;
    const target = past.slice(0, noteLimit).find(s => !s.hasNote);
    if (target) {
      autoOpenDoneRef.current = true;
      setActiveTab("sessions");
      setExpandedSession({ sessionId: target.id, mode: "add" });
      setNoteText("");
    }
  // autoOpenDoneRef evita re-ejecuciones; no hace falta seguir setActiveTab/derivations.length
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNote, sessionsState]);

  // Load emergency contact — fires in parallel with patient detail, no blocking spinner.
  useEffect(() => {
    if (!EMERGENCY_CONTACT_ENABLED) {
      setEcReady(true);
      return;
    }
    let cancelled = false;
    apiCoreAdapter.listEmergencyContacts(processId)
      .then((contacts) => {
        if (cancelled) return;
        setEcContacts(prev => prev.length > 0 ? prev : contacts.map((c) => {
          const data = { name: c.name, phone: c.phone, relationship: c.relationship };
          return { id: c.id, data, editMode: false, form: data, saving: false, deleting: false, deleteConfirming: false };
        }));
      })
      .catch(() => { /* leave state as-is */ })
      .finally(() => { if (!cancelled) setEcReady(true); });
    return () => { cancelled = true; };
  }, [processId]);

  const hasUnsavedEcChanges = ecContacts.some(entry => {
    if (!entry.editMode) return false;
    const hasContent = entry.form.name.trim() || entry.form.phone.trim() || entry.form.relationship.trim();
    if (!entry.data.name && !entry.data.phone && !entry.data.relationship) return !!hasContent;
    return entry.form.name !== entry.data.name || entry.form.phone !== entry.data.phone || entry.form.relationship !== entry.data.relationship;
  });

  const handleClose = () => {
    if (hasUnsavedEcChanges) { setShowUnsavedModal(true); } else { onClose(); }
  };

  const handleSaveContact = async (idx: number) => {
    const entry = ecContacts[idx];
    if (!entry.form.name.trim() && !entry.form.phone.trim() && !entry.form.relationship.trim()) return;
    setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, saving: true } : e));
    try {
      const payload = {
        name: entry.form.name.trim(),
        phone: entry.form.phone.trim(),
        relationship: entry.form.relationship.trim(),
      };
      const saved = entry.id
        ? await apiCoreAdapter.updateEmergencyContact(processId, entry.id, payload)
        : await apiCoreAdapter.createEmergencyContact(processId, payload);
      // Notificación al backoffice, opcional: el guardado real ya ocurrió arriba vía apiCore.
      if (EMERGENCY_CONTACT_WEBHOOK_ENABLED) {
        fetch(EMERGENCY_CONTACT_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ process_id: processId, ...payload }),
        }).catch((_err) => { /* ignore webhook errors */ });
      }
      setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, id: saved.id, data: payload, editMode: false, saving: false } : e));
      toast.success("Contacto de emergencia guardado");
    } catch {
      toast.error("No se pudo guardar el contacto de emergencia");
      setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, saving: false } : e));
    }
  };

  const addEmergencyContact = () => {
    setEcContacts(prev => [...prev, { id: null, data: { ...EC_EMPTY }, editMode: true, form: { ...EC_EMPTY }, saving: false, deleting: false, deleteConfirming: false }]);
  };

  const removeEcCard = (idx: number) => {
    setEcContacts(prev => prev.filter((_, i) => i !== idx));
  };

  const cancelEcEdit = (idx: number) => {
    const entry = ecContacts[idx];
    if (!entry.id) {
      // Tarjeta nueva, nunca guardada: descartarla directamente.
      removeEcCard(idx);
    } else {
      setEcContacts(prev => prev.map((ec, i) => i === idx ? { ...ec, editMode: false, form: ec.data } : ec));
    }
  };

  const requestDeleteContact = (idx: number) => {
    setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, deleteConfirming: true } : e));
  };

  const cancelDeleteContact = (idx: number) => {
    setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, deleteConfirming: false } : e));
  };

  const confirmDeleteContact = async (idx: number) => {
    const entry = ecContacts[idx];
    if (!entry.id) { removeEcCard(idx); return; }
    setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, deleting: true } : e));
    try {
      await apiCoreAdapter.deleteEmergencyContact(processId, entry.id);
      removeEcCard(idx);
      toast.success("Contacto de emergencia eliminado");
    } catch {
      toast.error("No se pudo eliminar el contacto de emergencia");
      setEcContacts(prev => prev.map((e, i) => i === idx ? { ...e, deleting: false, deleteConfirming: false } : e));
    }
  };

  // Focus textarea when expanding in add mode
  useEffect(() => {
    if (expandedSession?.mode === "add") {
      requestAnimationFrame(() => {
        noteInputRef.current?.focus();
      });
    }
  }, [expandedSession]);

  // Timezone difference label
  const tzDifferenceLabel = useMemo(() => {
    const patientTz = detail?.profile?.timezone;
    if (!patientTz) return null;
    const now = new Date();
    const pOff = getTimeZoneOffsetMinutes(now, patientTz);
    const meOff = getTimeZoneOffsetMinutes(now, therapistTz);
    if (pOff == null || meOff == null) return null;
    const diff = pOff - meOff;
    return `${formatDiffLabel(diff)} (hora paciente)`;
  }, [detail?.profile?.timezone, therapistTz]);

  // País de residencia con bandera del país declarado (el timezone es solo fallback:
  // puede ser de otro país y contradecir el texto que va al lado)
  const countryDisplay = useMemo(() => {
    const country = detail?.profile?.country;
    if (!country) return undefined;
    const iso2 =
      countryNameToISO2(country) || timezoneToCountryCode(detail?.profile?.timezone);
    return iso2 ? <><CountryFlag iso2={iso2} /> {country}</> : country;
  }, [detail?.profile?.country, detail?.profile?.timezone]);

  // Determina si/cómo se muestra la pestaña "Derivación" — ver DerivationContext.
  const derivationContext: DerivationContext | null = useMemo(() => {
    const formal = derivations[0]; // el endpoint devuelve la derivación más reciente (0 o 1)
    if (formal) {
      const matched = previousProcesses.find((p) => p.process_id === formal.from_process_id) ?? null;
      return { kind: "formal", derivation: formal, previousProcess: matched };
    }
    const candidates = previousProcesses
      .filter((p) => p.process_id !== processId)
      .filter((p) => !currentPsychologistId || p.psychologist?.id !== currentPsychologistId)
      .filter((p) => (p.session_statuses ?? []).some(isEffectiveSessionStatus))
      .sort((a, b) => {
        const aDate = new Date(a.last_session_date || a.activation_date || 0).getTime();
        const bDate = new Date(b.last_session_date || b.activation_date || 0).getTime();
        return bDate - aDate;
      });
    return candidates[0] ? { kind: "informal", previousProcess: candidates[0] } : null;
  }, [derivations, previousProcesses, processId, currentPsychologistId]);

  // Save note handler
  const handleSaveNote = async (sessionId: string) => {
    if (!noteText.trim()) return;
    try {
      setSavingNote(true);
      await apiCoreAdapter.updateSessionNote(sessionId, noteText.trim());
      setSessionsState((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, hasNote: true, note: noteText.trim() } : s
        )
      );
      setExpandedSession(null);
      setNoteText("");
      toast.success("Nota guardada correctamente");
    } catch {
      toast.error("No se pudo guardar la nota");
    } finally {
      setSavingNote(false);
    }
  };

  const { nextSession, pastSessions } = useMemo(() => {
    const now = new Date();
    const sorted = sessionsState.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const future = sorted.find((s) => new Date(s.date).getTime() > now.getTime());
    const past = sorted.filter((s) => new Date(s.date).getTime() <= now.getTime());
    return { nextSession: future ?? null, pastSessions: past };
  }, [sessionsState]);

  // Payment countdown for the next session (only when status is 'incomplete')
  const nextSessionStatus = (nextSession?.status || "").toLowerCase();
  const [paymentCountdown, setPaymentCountdown] = useState<PaymentCountdownInfo | null>(null);

  useEffect(() => {
    if (!nextSession || nextSessionStatus !== "incomplete") {
      setPaymentCountdown(null);
      return;
    }
    const update = () => {
      const start = parseBackendSessionDate(nextSession.date, BA_TIMEZONE);
      setPaymentCountdown(getPaymentCountdown(start, BA_TIMEZONE));
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [nextSession, nextSessionStatus]);

  const isNewPatient = detail && (detail.totalSessions === 0 || detail.totalSessions === 1) && !detail.isActive;
  const statusLabel = detail
    ? isNewPatient
      ? "Nuevo"
      : detail.status === "inactive"
        ? "Inactivo"
        : detail.isActive
          ? "Activo"
          : null
    : null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-foreground">{patient.name || detail?.fullname}</h3>
              {detail?.isUrgent && (
                <span
                  data-testid="urgent-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                  Urgente
                </span>
              )}
            </div>
            {(patient.service || detail?.therapyType || statusLabel) && (
              <p className="text-[0.8125rem] text-muted-foreground mt-0.5">
                {[patient.service || detail?.therapyType, statusLabel].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1 -mt-0.5"
            aria-label="Cerrar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-border px-5">
          {(["info", "sessions", ...(derivationContext ? ["derivation" as const] : []), "clinical_history"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={cn(
                "py-2.5 px-4 text-[0.8125rem] font-medium transition-colors relative",
                activeTab === tab
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "info" ? "Información" : tab === "sessions" ? "Sesiones" : tab === "derivation" ? "Derivación" : "Historia clínica"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-[350px]">
          {isLoading && activeTab === "info" ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-[0.8125rem] text-muted-foreground">Cargando...</div>
            </div>
          ) : (isLoading || isLoadingSessions) && activeTab === "sessions" ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-[0.8125rem] text-muted-foreground">Cargando sesiones...</div>
            </div>
          ) : (derivationsLoading || previousProcessesLoading) && activeTab === "derivation" ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-[0.8125rem] text-muted-foreground">Cargando derivación...</div>
            </div>
          ) : sessionNotesLoading && activeTab === "clinical_history" ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-[0.8125rem] text-muted-foreground">Cargando notas...</div>
            </div>
          ) : activeTab === "derivation" ? (
            <DerivationTab context={derivationContext} sessionNotes={sessionNotes} />
          ) : activeTab === "clinical_history" ? (
            <ClinicalHistoryTab
              notes={sessionNotes}
              derivations={derivations}
              onSwitchToDerivation={() => setActiveTab("derivation")}
              isImpersonated={isImpersonated()}
              patientName={patient.name || detail?.fullname || ""}
              patientEmail={patient.email || detail?.email || ""}
              therapyType={patient.service || detail?.therapyType}
              processStatus={statusLabel}
            />
          ) : activeTab === "info" ? (
            <div className="space-y-5">
              {/* Motivo de consulta */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Motivo de consulta</h4>
                <div className="bg-muted/50 rounded-lg p-3">
                  <div
                    ref={reasonRef}
                    className="text-[0.8125rem] text-foreground whitespace-pre-wrap"
                    style={{
                      overflow: "hidden",
                      display: reasonExpanded ? "block" : ("-webkit-box" as unknown as "block"),
                      WebkitLineClamp: reasonExpanded ? (undefined as unknown as number) : 3,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {detail?.reason || "—"}
                  </div>
                  {detail?.reason && reasonCanToggle && (
                    <button
                      type="button"
                      className="mt-1.5 text-primary text-[0.8125rem] font-medium underline underline-offset-2 hover:text-primary/80"
                      onClick={() => setReasonExpanded(!reasonExpanded)}
                    >
                      {reasonExpanded ? "Ver menos" : "Ver más"}
                    </button>
                  )}
                </div>
              </div>

              {/* Sobre el paciente */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Sobre el paciente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <InfoField label="Fecha de nacimiento" value={formatBirthDateDdMmYyyy(detail?.profile?.birthDate)} />
                  <InfoField label="País de residencia" value={countryDisplay} />
                  <InfoField
                    label="Huso horario"
                    value={detail?.profile?.timezone ? <><CountryFlag timezone={detail.profile.timezone} /> {detail.profile.timezone}</> : undefined}
                  />
                  <InfoField label="Diferencia horaria" value={tzDifferenceLabel} />
                  {detail?.isUrgent && (
                    <InfoField
                      label="Teléfono"
                      value={detail?.phone ?? undefined}
                    />
                  )}
                </div>
              </div>

              {/* Preferencias del proceso */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Preferencias del proceso</h4>
                <div className="grid grid-cols-2 gap-3">
                  <InfoField label="Enfoque terapéutico" value={detail?.preferences?.approach || "Indistinto"} />
                  <InfoField
                    label="Forma de pago"
                    value={detail?.automaticDebit === false ? "Manual" : "Automático"}
                  />
                </div>
              </div>

              {/* Contacto de emergencia — al final, sin spinner (carga en background) */}
              <div data-testid="emergency-contact-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-foreground">Contacto de emergencia</h4>
                  <button
                    type="button"
                    data-testid="ec-add-btn"
                    onClick={addEmergencyContact}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    <span className="text-base leading-none">+</span> Agregar
                  </button>
                </div>
                {ecReady && ecContacts.length === 0 && (
                  <p className="text-sm text-muted-foreground">Sin contacto de emergencia registrado.</p>
                )}
                {ecContacts.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {ecContacts.map((entry, idx) => (
                      <div key={entry.id ?? `new-${idx}`} className="flex-1 min-w-[180px] rounded-xl border border-border bg-muted/30 p-3 space-y-2">
                        {/* Card header: label + X (solo en edición: descarta la tarjeta nueva o revierte la edición) */}
                        <div className="flex items-center justify-between">
                          <span className="text-[0.7rem] font-semibold text-muted-foreground uppercase tracking-wide">
                            Contacto {ecContacts.length > 1 ? idx + 1 : ""}
                          </span>
                          {entry.editMode && (
                            <button
                              type="button"
                              aria-label="Cerrar card"
                              data-testid={`ec-cancel-btn-${idx}`}
                              onClick={() => cancelEcEdit(idx)}
                              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </div>

                        {entry.editMode ? (
                          <div data-testid={`ec-form-${idx}`}>
                            <input
                              type="text"
                              data-testid={`ec-input-name-${idx}`}
                              placeholder="Nombre completo"
                              value={entry.form.name}
                              onChange={(e) => setEcContacts(prev => prev.map((ec, i) => i === idx ? { ...ec, form: { ...ec.form, name: e.target.value } } : ec))}
                              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[0.8125rem] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 transition mb-2"
                            />
                            <input
                              type="text"
                              data-testid={`ec-input-phone-${idx}`}
                              placeholder="Teléfono (con código de país)"
                              value={entry.form.phone}
                              onChange={(e) => setEcContacts(prev => prev.map((ec, i) => i === idx ? { ...ec, form: { ...ec.form, phone: e.target.value } } : ec))}
                              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[0.8125rem] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 transition mb-2"
                            />
                            <input
                              type="text"
                              data-testid={`ec-input-relationship-${idx}`}
                              placeholder="Relación (ej. Madre, Pareja...)"
                              value={entry.form.relationship}
                              onChange={(e) => setEcContacts(prev => prev.map((ec, i) => i === idx ? { ...ec, form: { ...ec.form, relationship: e.target.value } } : ec))}
                              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[0.8125rem] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 transition mb-2"
                            />
                            <button
                              type="button"
                              data-testid={`ec-save-btn-${idx}`}
                              disabled={entry.saving || (!entry.form.name.trim() && !entry.form.phone.trim() && !entry.form.relationship.trim())}
                              onClick={() => handleSaveContact(idx)}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[0.8125rem] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {entry.saving ? "Guardando..." : "Guardar contacto"}
                            </button>
                          </div>
                        ) : (
                          <div data-testid={`ec-readonly-${idx}`} className="space-y-1.5">
                            <div>
                              <p className="text-[0.7rem] text-muted-foreground">Nombre</p>
                              <p className="text-[0.8125rem] font-medium text-foreground">{entry.data.name}</p>
                            </div>
                            <div>
                              <p className="text-[0.7rem] text-muted-foreground">Teléfono</p>
                              <p className="text-[0.8125rem] font-medium text-foreground">{entry.data.phone}</p>
                            </div>
                            <div>
                              <p className="text-[0.7rem] text-muted-foreground">Relación</p>
                              <p className="text-[0.8125rem] font-medium text-foreground">{entry.data.relationship}</p>
                            </div>
                            {entry.deleteConfirming ? (
                              <div className="flex items-center gap-2 pt-0.5">
                                <span className="text-[0.75rem] text-muted-foreground">¿Eliminar?</span>
                                <button
                                  type="button"
                                  data-testid={`ec-delete-confirm-btn-${idx}`}
                                  disabled={entry.deleting}
                                  className="text-[0.75rem] font-medium text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                                  onClick={() => confirmDeleteContact(idx)}
                                >
                                  {entry.deleting ? "Eliminando..." : "Sí"}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`ec-delete-cancel-btn-${idx}`}
                                  disabled={entry.deleting}
                                  className="text-[0.75rem] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                                  onClick={() => cancelDeleteContact(idx)}
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 pt-0.5">
                                <button
                                  type="button"
                                  data-testid={`ec-edit-btn-${idx}`}
                                  className="inline-flex items-center gap-1 text-[0.75rem] text-primary hover:text-primary/80 transition-colors"
                                  onClick={() => setEcContacts(prev => prev.map((ec, i) => i === idx ? { ...ec, editMode: true, form: ec.data } : ec))}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  data-testid={`ec-delete-btn-${idx}`}
                                  className="inline-flex items-center gap-1 text-[0.75rem] text-muted-foreground hover:text-red-500 transition-colors"
                                  onClick={() => requestDeleteContact(idx)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                  Eliminar
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Constancia — oculta hasta que CONSTANCIA_FEATURE_ENABLED se reactive */}
              {CONSTANCIA_FEATURE_ENABLED && (
                <div className="flex items-center justify-end gap-2" data-testid="constancia-section">
                  <InfoTooltip
                    text="Si el/la paciente necesita un informe clínico o terapéutico, ese documento es responsabilidad de la profesional tratante y no puede generarse desde la plataforma."
                    ariaLabel="Aclaración sobre la Constancia"
                    placement="left"
                  />
                  <button
                    type="button"
                    data-testid="generate-constancia-btn"
                    disabled={generatingConstancia || fullHistoryLoading || constanciaSegments.length === 0}
                    onClick={handleGenerateConstancia}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-[0.8125rem] font-medium text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    {generatingConstancia ? "Generando..." : "Generar Constancia"}
                  </button>
                  {!fullHistoryLoading && constanciaSegments.length === 0 && (
                    <span data-testid="constancia-empty-hint" className="text-xs text-muted-foreground">
                      Sin sesiones pagadas registradas
                    </span>
                  )}
                </div>
              )}

              {/* Próxima sesión */}
              {nextSession && (() => {
                const argTime = formatDateInTimezone(nextSession.date, therapistTz);
                const patientTz = detail?.profile?.timezone;
                const patientIso2 = patientTz ? timezoneToCountryCode(patientTz) : null;
                const patientTzLabel = patientTz
                  ? (patientTz.split("/").pop()?.replace(/_/g, " ") ?? patientTz)
                  : null;
                // Show patient timezone always when available — even if same as Argentina.
                const patientTime = patientTz ? formatDateInTimezone(nextSession.date, patientTz) : null;
                const sessionStatus = (nextSession.status || "").toLowerCase();
                const paymentLabel = sessionStatus === "incomplete" ? "Pendiente" : sessionStatus === "active" ? "1er pago" : null;
                return (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Próxima sesión</h4>
                    <div className="border border-border rounded-lg px-4 py-3 bg-primary/5">
                      <div className="flex items-center justify-between">
                        <div className="text-[0.8125rem] font-medium text-foreground">{argTime.date}</div>
                        {paymentLabel && (
                          <span className={cn(
                            "text-xs font-medium px-2.5 py-0.5 rounded-full",
                            sessionStatus === "incomplete"
                              ? "bg-[#FAF7EE] text-[#B4700B] border border-[#e9c87a]"
                              : "bg-emerald-500/10 text-emerald-600"
                          )}>
                            {paymentLabel}
                          </span>
                        )}
                      </div>
                      <div
                        data-testid="patient-detail-next-ar-time"
                        className="flex items-center gap-1 text-xs text-muted-foreground"
                      >
                        <CountryFlag timezone={therapistTz} fallbackIso2="AR" size="0.75rem" />
                        <span>{argTime.day} · {argTime.time} ({therapistCountry})</span>
                      </div>
                      {patientTime && (
                        <div
                          data-testid="patient-detail-next-patient-tz"
                          className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          {patientIso2 && <CountryFlag iso2={patientIso2} size="0.75rem" />}
                          <span>{patientTime.day} · {patientTime.time} ({patientTzLabel})</span>
                        </div>
                      )}
                      {paymentCountdown && !paymentCountdown.isExpired && (
                        <div className={cn(
                          "text-xs font-medium mt-1.5 flex items-center gap-1.5",
                          paymentCountdown.isUrgent ? "text-orange-500" : "text-amber-600"
                        )}>
                          <span className="flex-shrink-0">⏱</span>
                          <span data-testid="patient-detail-payment-countdown">{paymentCountdown.countdownLabel}</span>
                          <Tooltip delayDuration={150}>
                            <TooltipTrigger asChild>
                              <span
                                data-testid="payment-policy-info-btn"
                                className="inline-flex items-center flex-shrink-0 opacity-60 hover:opacity-100 cursor-default"
                                aria-label="Política de pago"
                              >
                                <Info size={12} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" sideOffset={6} className="w-[280px] py-3 space-y-2 text-left leading-relaxed">
                              <p className="font-semibold text-foreground text-[0.8125rem]">Política de pago y cancelación</p>
                              <ul className="list-disc pl-4 space-y-1 text-xs">
                                <li>El pago debe realizarse hasta 12 horas antes del horario de la sesión.</li>
                                <li>Si no se paga en ese plazo, la sesión se cancela automáticamente.</li>
                                <li>Una sesión cancelada por falta de pago no puede realizarse el mismo día.</li>
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Sesiones pasadas */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-foreground">Sesiones pasadas</h4>
                  <span className="text-[0.8125rem] text-muted-foreground font-medium">
                    {pastSessions.length} sesiones
                  </span>
                </div>

                {pastSessions.length > 0 ? (
                  <div className="border border-border rounded-lg overflow-hidden">
                    {pastSessions.map((s, idx) => {
                      const patientTz = detail?.profile?.timezone;
                      const patientIso2 = patientTz ? timezoneToCountryCode(patientTz) : null;
                      const patientTzLabel = patientTz
                        ? (patientTz.split("/").pop()?.replace(/_/g, " ") ?? patientTz)
                        : null;
                      const argTime = formatDateInTimezone(s.date, therapistTz);
                      // Show patient timezone always when available — even if same as Argentina.
                      const patientTime = patientTz ? formatDateInTimezone(s.date, patientTz) : null;
                      const isExpanded = expandedSession?.sessionId === s.id;
                      const canAddNote = derivations.length > 0 || idx < 6;

                      return (
                        <div key={s.id} className="border-b border-border last:border-b-0">
                          <div className="flex items-center justify-between px-4 py-3">
                            <div>
                              <div className="text-[0.8125rem] font-medium text-foreground">{argTime.date}</div>
                              <div
                                data-testid="patient-detail-past-ar-time"
                                className="flex items-center gap-1 text-xs text-muted-foreground"
                              >
                                <CountryFlag timezone={therapistTz} fallbackIso2="AR" size="0.75rem" />
                                <span>{argTime.day} · {argTime.time} ({therapistCountry})</span>
                              </div>
                              {patientTime && (
                                <div
                                  data-testid="patient-detail-past-patient-tz"
                                  className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                  {patientIso2 && <CountryFlag iso2={patientIso2} size="0.75rem" />}
                                  <span>{patientTime.day} · {patientTime.time} ({patientTzLabel})</span>
                                </div>
                              )}
                            </div>
                            <div>
                              {s.hasNote ? (
                                <button
                                  type="button"
                                  className="h-[35px] px-[0.875rem] rounded-full text-[0.8125rem] font-medium bg-muted text-muted-foreground border border-border hover:bg-muted/80 whitespace-nowrap"
                                  onClick={() => {
                                    if (isExpanded && expandedSession?.mode === "view") {
                                      setExpandedSession(null);
                                    } else {
                                      setExpandedSession({ sessionId: s.id, mode: "view" });
                                    }
                                  }}
                                >
                                  {isExpanded && expandedSession?.mode === "view" ? "Ocultar nota" : "Ver nota"}
                                </button>
                              ) : canAddNote ? (
                                <button
                                  type="button"
                                  className="h-[35px] px-[0.875rem] rounded-full text-[0.8125rem] font-medium border-2 border-primary text-primary bg-white hover:bg-primary/5 whitespace-nowrap"
                                  onClick={() => {
                                    if (isExpanded && expandedSession?.mode === "add") {
                                      setExpandedSession(null);
                                      setNoteText("");
                                    } else {
                                      setExpandedSession({ sessionId: s.id, mode: "add" });
                                      setNoteText("");
                                    }
                                  }}
                                >
                                  Agregar nota
                                </button>
                              ) : null}
                            </div>
                          </div>

                          {/* Inline expanded note */}
                          {isExpanded && expandedSession?.mode === "view" && (
                            <div className="px-4 pb-3">
                              <div className="bg-muted/50 border border-border rounded-lg p-3">
                                <p className="text-[0.8125rem] text-foreground whitespace-pre-wrap">
                                  {s.note || "—"}
                                </p>
                              </div>
                              <div className="flex justify-end mt-2">
                                <button
                                  type="button"
                                  className="h-[35px] px-[0.875rem] rounded-full text-[0.8125rem] font-medium border-2 border-primary text-primary bg-white hover:bg-primary/5 whitespace-nowrap"
                                  onClick={() => {
                                    setNoteText(s.note || "");
                                    setExpandedSession({ sessionId: s.id, mode: "add" });
                                  }}
                                >
                                  Actualizar
                                </button>
                              </div>
                            </div>
                          )}

                          {isExpanded && expandedSession?.mode === "add" && (
                            <div className="px-4 pb-3">
                              <textarea
                                ref={noteInputRef}
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Escribir nota..."
                                className="w-full min-h-[100px] rounded-lg border border-border p-3 text-[0.8125rem] text-foreground resize-vertical outline-none focus:ring-1 focus:ring-primary"
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  className="h-[35px] px-[0.875rem] rounded-full text-[0.8125rem] font-medium text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    setExpandedSession(null);
                                    setNoteText("");
                                  }}
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="button"
                                  disabled={savingNote || !noteText.trim()}
                                  className="h-[35px] px-[0.875rem] rounded-full text-[0.8125rem] font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleSaveNote(s.id)}
                                >
                                  {savingNote ? "Guardando..." : "Guardar nota"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <p className="text-sm font-semibold text-foreground">
                      Aquí verás las sesiones pasadas
                    </p>
                    <p className="text-[0.8125rem] text-muted-foreground mt-1">
                      Podrás agregar notas luego de cada sesión.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </DialogContent>

      {/* Unsaved changes confirmation */}
      {showUnsavedModal && (
        <Dialog open onOpenChange={() => setShowUnsavedModal(false)}>
          <DialogContent className="max-w-sm p-6 flex flex-col gap-4">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Tenés cambios no guardados</h3>
              <p className="text-sm text-muted-foreground">¿Querés salir sin guardar el contacto de emergencia?</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowUnsavedModal(false)}
                className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                Seguir editando
              </button>
              <button
                type="button"
                onClick={() => { setShowUnsavedModal(false); onClose(); }}
                className="inline-flex items-center rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                Salir sin guardar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

// --- Derivation tab ---

const normalizeLineBreaks = (text: string) => text.replace(/\s*\|\s*/g, '\n').replace(/\\n/g, '\n');

const formatLongDate = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "long", year: "numeric" }).format(d);
};

const personName = (p?: { name?: string; surname?: string } | null): string | undefined => {
  const full = [p?.name, p?.surname].filter(Boolean).join(" ").trim();
  return full || undefined;
};

const REQUESTER_LABEL: Record<string, string> = {
  PATIENT: "Solicitada por el paciente",
  PSYCHOLOGIST: "Solicitada por la psicóloga anterior",
};

const DERIVATION_TYPE_LABEL: Record<string, string> = {
  INTERNAL: "Derivación interna",
  EXTERNAL: "Derivación externa",
};

/** Card colapsable de notas de un proceso — mismo componente/fuente de datos (`sessionNotes`) que usa Historia clínica. */
function ProcessNotesCard({ psychologistName, notes }: { psychologistName?: string; notes: SessionNote[] }) {
  const [isOpen, setIsOpen] = useState(true);
  if (notes.length === 0) return null;
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setIsOpen((o) => !o)}
      >
        <span className="text-[0.8125rem] font-semibold text-foreground">Psico {psychologistName || "Psicóloga"}</span>
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          {notes.length} {notes.length === 1 ? "nota" : "notas"}
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${isOpen ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9"/></svg>
        </span>
      </button>
      {isOpen && (
        <div className="divide-y divide-border">
          {notes.map((n) => (
            <div key={n.session_id} className="px-4 py-3">
              <div className="text-xs text-muted-foreground mb-1.5">{formatLongDate(n.date)}</div>
              <p className="text-[0.8125rem] text-foreground whitespace-pre-wrap">{n.notes}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h4 className="text-sm font-semibold text-foreground mb-2">{children}</h4>;
}

function DerivationTab({ context, sessionNotes }: { context: DerivationContext | null; sessionNotes: SessionNote[] }) {
  if (!context) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm font-semibold text-foreground">Sin derivaciones registradas</p>
      </div>
    );
  }

  const isFormal = context.kind === "formal";
  const derivation = isFormal ? context.derivation : null;
  const previousProcess = context.previousProcess;
  const previousProcessId = isFormal ? derivation!.from_process_id : previousProcess?.process_id;
  const previousPsychologistName =
    personName(previousProcess?.psychologist) ?? personName(derivation?.from_psychologist) ?? "Psicóloga";

  // --- Encabezado + badge secundario ---
  const badgeLabel = isFormal
    ? (derivation!.type && DERIVATION_TYPE_LABEL[derivation!.type]) || "Derivación"
    : "Baja y retorno";
  const badgeSubLabel = isFormal && derivation!.requester ? REQUESTER_LABEL[derivation!.requester] : undefined;

  // --- Sección (a): información con la que llegó el paciente (solo derivación formal) ---
  const derivationInfoFields = isFormal
    ? [
        derivation!.notes && derivation!.notes.trim().toLowerCase() !== "test"
          ? { label: "Resumen de la derivación", value: normalizeLineBreaks(derivation!.notes) }
          : null,
        derivation!.diagnostic ? { label: "Diagnóstico", value: derivation!.diagnostic } : null,
        derivation!.epicrisis ? { label: "Epicrisis", value: derivation!.epicrisis } : null,
        derivation!.preference ? { label: "Preferencia informada", value: derivation!.preference } : null,
        derivation!.suggestion
          ? { label: "Comentarios para nueva terapeuta", value: normalizeLineBreaks(derivation!.suggestion), scroll: true }
          : null,
      ].filter((f): f is { label: string; value: string; scroll?: boolean } => !!f)
    : [];
  const showInfoSection = isFormal && derivationInfoFields.length > 0;

  // --- Sección (b): proceso anterior ---
  const dateRange = [formatLongDate(previousProcess?.activation_date), formatLongDate(previousProcess?.last_session_date)]
    .filter(Boolean)
    .join(" – ") || undefined;
  const effectiveSessionCount = previousProcess?.session_statuses
    ? previousProcess.session_statuses.filter(isEffectiveSessionStatus).length
    : undefined;
  const fallbackSessionCount = effectiveSessionCount === undefined ? derivation?.from_session_count : undefined;
  const previousProcessNotes = previousProcessId ? sessionNotes.filter((n) => n.process_id === previousProcessId) : [];
  const showPreviousProcessSection = true; // siempre hay al menos "psicóloga anterior"

  // --- Sección (c): motivo ---
  const formalReason = isFormal ? derivation!.reason : null;
  const informalReason = !isFormal ? context.previousProcess.status_change_reason : undefined;
  const showReasonSection = isFormal ? !!formalReason?.name : !!informalReason;

  return (
    <div className="space-y-5">
      {/* Encabezado */}
      <div>
        <p className="text-[0.8125rem] font-semibold text-foreground">
          Este paciente ya tuvo un proceso anterior con {previousPsychologistName}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
            {badgeLabel}
          </span>
          {badgeSubLabel && <span className="text-xs text-muted-foreground">{badgeSubLabel}</span>}
        </div>
      </div>

      {/* (a) Información con la que llegó el paciente — solo derivación formal */}
      {showInfoSection && (
        <div>
          <SectionTitle>Información con la que llegó el paciente</SectionTitle>
          <div className="space-y-3">
            {derivationInfoFields.map((f) => (
              <div key={f.label}>
                <div className="text-xs font-medium text-muted-foreground mb-0.5">{f.label}</div>
                <div className={cn("bg-muted/50 rounded-lg p-3", f.scroll && "max-h-48 overflow-y-auto")}>
                  <p className="text-[0.8125rem] text-foreground whitespace-pre-wrap">{f.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* (b) Proceso anterior */}
      {showPreviousProcessSection && (
        <div>
          <SectionTitle>Proceso anterior</SectionTitle>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <InfoField label="Psicóloga" value={previousPsychologistName} />
              {dateRange && <InfoField label="Rango de fechas" value={dateRange} />}
              {effectiveSessionCount !== undefined && (
                <InfoField label="Sesiones efectivas" value={String(effectiveSessionCount)} />
              )}
              {effectiveSessionCount === undefined && fallbackSessionCount !== undefined && (
                <InfoField label="Cantidad de sesiones" value={String(fallbackSessionCount)} />
              )}
            </div>
            <ProcessNotesCard psychologistName={previousPsychologistName} notes={previousProcessNotes} />
          </div>
        </div>
      )}

      {/* (c) Motivo */}
      {showReasonSection && (
        <div>
          <SectionTitle>Motivo</SectionTitle>
          {isFormal ? (
            <div className="text-[0.8125rem] text-foreground space-y-1">
              <p>
                <span className="font-medium">{formalReason!.name}</span>
                {badgeSubLabel && <span className="text-muted-foreground"> — {badgeSubLabel}</span>}
              </p>
              {formalReason?.description && (
                <p className="text-muted-foreground whitespace-pre-wrap">{formalReason.description}</p>
              )}
            </div>
          ) : (
            <p className="text-[0.8125rem] text-foreground">
              El paciente dejó su proceso anterior con {previousPsychologistName} por: {informalReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// --- Historia clínica tab ---

type SessionNote = { session_id: string; date: string; notes: string; process_id: string; psychologist_name: string; psychologist_discharged: boolean };

interface ClinicalHistoryTabProps {
  notes: SessionNote[];
  derivations: ProcessDerivation[];
  onSwitchToDerivation: () => void;
  isImpersonated?: boolean;
  patientName?: string;
  patientEmail?: string;
  therapyType?: string | null;
  processStatus?: string | null;
}

function ClinicalHistoryTab({
  notes,
  derivations,
  onSwitchToDerivation,
  isImpersonated: impersonated,
  patientName = "",
  patientEmail = "",
  therapyType,
  processStatus,
}: ClinicalHistoryTabProps) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<string, { psychologist_name: string; psychologist_discharged: boolean; notes: SessionNote[] }>();
    for (const n of notes) {
      if (!map.has(n.process_id)) map.set(n.process_id, { psychologist_name: n.psychologist_name, psychologist_discharged: n.psychologist_discharged, notes: [] });
      map.get(n.process_id)!.notes.push(n);
    }
    return Array.from(map.entries()).map(([process_id, g]) => ({ process_id, ...g }));
  }, [notes]);

  useEffect(() => {
    if (groups.length > 0) setOpenGroups(new Set([groups[0].process_id]));
  }, [groups]);

  const toggle = (process_id: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(process_id) ? next.delete(process_id) : next.add(process_id);
      return next;
    });
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      exportClinicalHistoryPdf({
        patientName,
        patientEmail,
        therapyType,
        processStatus,
        notes,
      });
    } finally {
      setExporting(false);
    }
  };

  if (notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
        <p className="text-sm font-semibold text-foreground">Sin notas registradas</p>
        <p className="text-xs text-muted-foreground">No hay notas de sesión para este paciente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Export button — only for impersonation sessions */}
      {impersonated && (
        <div className="flex justify-end pb-1">
          <button
            data-testid="export-clinical-history-btn"
            type="button"
            disabled={exporting}
            onClick={handleExportPdf}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-[0.8125rem] font-medium text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {exporting ? "Generando PDF..." : "Exportar historial"}
          </button>
        </div>
      )}
      {groups.map(({ process_id, psychologist_name, psychologist_discharged, notes: groupNotes }) => {
        const isOpen = openGroups.has(process_id);
        const hasDerivation = derivations.some(d => d.from_process_id === process_id);
        return (
          <div key={process_id} className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              onClick={() => toggle(process_id)}
            >
              <span className="text-[0.8125rem] font-semibold text-foreground flex items-center gap-1.5">
                {hasDerivation ? (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <span
                        className="underline decoration-dotted cursor-pointer hover:text-primary"
                        onClick={e => { e.stopPropagation(); onSwitchToDerivation(); }}
                      >
                        Psico {psychologist_name || "Psicóloga"}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px] text-center leading-snug">
                      Este proceso terapéutico tiene notas de derivación, clickeá acá para verlas
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <>Psico {psychologist_name || "Psicóloga"}</>
                )}
                {psychologist_discharged && <span className="text-xs font-normal text-muted-foreground">(inactiva)</span>}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                {groupNotes.length} {groupNotes.length === 1 ? "nota" : "notas"}
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${isOpen ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </button>
            {isOpen && (
              <div className="divide-y divide-border">
                {groupNotes.map((n) => {
                  const dateLabel = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "long", year: "numeric" }).format(new Date(n.date));
                  return (
                    <div key={n.session_id} className="px-4 py-3">
                      <div className="text-xs text-muted-foreground mb-1.5">{dateLabel}</div>
                      <p className="text-[0.8125rem] text-foreground whitespace-pre-wrap">{n.notes}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- Small helper component ---

function InfoField({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground text-[0.8125rem] font-medium">{label}</div>
      <div className="text-foreground text-[0.8125rem]">{value || "—"}</div>
    </div>
  );
}
